import HawkCatcher from '@hawk.so/javascript';
import { getUserId } from './userIdService';

/**
 * Сервис для интеграции с Hawk.so - системой отслеживания ошибок
 * 
 * Hawk автоматически перехватывает:
 * - Необработанные ошибки (unhandled errors)
 * - Отклоненные промисы (unhandled promise rejections)
 * - Ошибки в консоли (если включено consoleTracking)
 * 
 * Примеры использования:
 * 
 * 1. Автоматический перехват (работает из коробки):
 *    - Любые необработанные ошибки автоматически отправляются в Hawk
 * 
 * 2. Ручная отправка ошибки:
 *    import { hawkService } from './services/hawkService';
 *    
 *    try {
 *      // какой-то код
 *    } catch (error) {
 *      hawkService.catch(error, {
 *        component: 'MyComponent',
 *        action: 'submitForm',
 *        additionalData: { formId: '123' }
 *      });
 *      throw error; // пробрасываем дальше, если нужно
 *    }
 *    Примечание: метод catch() внутри использует hawk.send() - это правильный API Hawk
 * 
 * 3. Отправка строки как ошибки:
 *    hawkService.catch('Произошла критическая ошибка при загрузке данных', {
 *      userId: 'user-123',
 *      page: window.location.pathname
 *    });
 * 
 * 4. Добавление breadcrumb для отслеживания действий:
 *    hawkService.addBreadcrumb('Пользователь нажал кнопку отправки', 'user-action', {
 *      buttonId: 'submit-btn'
 *    });
 */
class HawkService {
  private hawk: HawkCatcher | null = null;
  private isInitialized = false;

  /**
   * Инициализирует Hawk Catcher с токеном из переменных окружения
   * 
   * @param options - Дополнительные опции конфигурации
   */
  init(options?: {
    release?: string;
    debug?: boolean;
    consoleTracking?: boolean;
    disableGlobalErrorsHandling?: boolean;
  }) {
    // Проверяем, что Hawk не был уже инициализирован
    if (this.isInitialized) {
      console.warn('Hawk уже инициализирован. Повторная инициализация пропущена.');
      return;
    }

    // Отключение через переменные окружения (VITE_HAWK_ENABLED или HAWK_ENABLED при сборке)
    const hawkEnabled = import.meta.env.VITE_HAWK_ENABLED;
    if (hawkEnabled === 'false') {
      console.warn('Hawk отключён через VITE_HAWK_ENABLED=false (или HAWK_ENABLED=false при сборке).');
      return;
    }

    // Получаем токен из переменных окружения
    // Поддерживаются оба варианта: VITE_HAWK_TOKEN_FRONTEND (для Vite) и HAWK_TOKEN_FRONTEND (через vite.config.ts)
    const token = import.meta.env.VITE_HAWK_TOKEN_FRONTEND;

    // Если токен не указан, Hawk не будет инициализирован
    if (!token) {
      console.warn('VITE_HAWK_TOKEN_FRONTEND или HAWK_TOKEN_FRONTEND не установлен. Hawk не будет инициализирован.');
      return;
    }

    try {
      // Получаем информацию о пользователе (если доступна)
      const userId = getUserId();

      // Инициализируем Hawk Catcher
      this.hawk = new HawkCatcher({
        token,
        release: options?.release || import.meta.env.VITE_APP_VERSION || '1.0.0',
        user: userId ? {
          id: userId,
          // Дополнительная информация о пользователе может быть добавлена позже
        } : undefined,
        context: {
          environment: import.meta.env.MODE || 'development',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        },
        debug: options?.debug || import.meta.env.MODE === 'development',
        consoleTracking: options?.consoleTracking !== undefined 
          ? options.consoleTracking 
          : import.meta.env.MODE === 'production',
        disableGlobalErrorsHandling: options?.disableGlobalErrorsHandling || false,
      });

      this.isInitialized = true;
      console.log('Hawk успешно инициализирован');
      
      // Делаем hawkService доступным в window для тестирования из консоли
      if (typeof window !== 'undefined') {
        (window as any).hawkService = this;
        (window as any).hawk = this.hawk;
      }
    } catch (error) {
      console.error('Ошибка при инициализации Hawk:', error);
    }
  }

  /**
   * Возвращает экземпляр Hawk Catcher (если инициализирован)
   */
  getInstance(): HawkCatcher | null {
    return this.hawk;
  }

  /**
   * Вручную отправляет ошибку в Hawk
   * 
   * @param error - Ошибка для отправки
   * @param context - Дополнительный контекст
   */
  catch(error: Error | string, context?: Record<string, any>) {
    if (!this.hawk) {
      console.warn('Hawk не инициализирован. Ошибка не отправлена:', error);
      console.warn('Проверьте, что VITE_HAWK_TOKEN_FRONTEND или HAWK_TOKEN_FRONTEND установлен в .env');
      return;
    }

    try {
      console.log('[Hawk] Отправка ошибки в Hawk:', error);
      // Используем метод .send() вместо .catch() - это правильный API Hawk JavaScript
      this.hawk.send(error, context);
      console.log('[Hawk] Ошибка отправлена успешно');
    } catch (err) {
      console.error('[Hawk] Ошибка при отправке ошибки в Hawk:', err);
    }
  }

  /**
   * Обновляет информацию о пользователе
   * 
   * @param user - Информация о пользователе
   */
  setUser(user: { id: string; name?: string; email?: string; image?: string }) {
    if (!this.hawk) {
      console.warn('Hawk не инициализирован. Невозможно обновить информацию о пользователе.');
      return;
    }

    try {
      // Hawk не имеет прямого метода setUser, но мы можем обновить контекст
      // или переинициализировать с новыми данными пользователя
      // Для простоты, просто логируем предупреждение
      console.warn('Метод setUser требует переинициализации Hawk с новыми данными пользователя');
    } catch (error) {
      console.error('Ошибка при обновлении информации о пользователе:', error);
    }
  }

  /**
   * Добавляет breadcrumb (след) для отслеживания действий пользователя
   * 
   * @param message - Сообщение breadcrumb
   * @param category - Категория действия
   * @param data - Дополнительные данные
   */
  addBreadcrumb(message: string, category?: string, data?: Record<string, any>) {
    if (!this.hawk) {
      return;
    }

    try {
      // Hawk автоматически собирает breadcrumbs из консоли и действий
      // Для ручного добавления можно использовать контекст
      console.debug('[Hawk Breadcrumb]', { message, category, data });
    } catch (error) {
      console.error('Ошибка при добавлении breadcrumb:', error);
    }
  }

  /**
   * Проверяет, инициализирован ли Hawk
   */
  isReady(): boolean {
    return this.isInitialized && this.hawk !== null;
  }
}

// Экспортируем singleton экземпляр
export const hawkService = new HawkService();
